import { datadogRum } from '@datadog/browser-rum'
import { ApolloError } from '@apollo/client'
import {
  LoadersType,
  ErrorMessageTranslation,
  Localization,
} from '../../interfaces'
import {
  setSessionData,
  manageErrors,
  removeLoader,
  addLoader,
  setCheckoutRules,
} from '../../redux/slices/config'
import useAppDispatch from '../useAppDispatch'
import { useAdvancedFilters } from '../filters/useFilters'
import { setBanners } from '../../redux/slices/banners/banners'
import castBanners from '../../utils/castBanners'
import { onCartResponse } from '../../utils/onCartResponse'
import useAppSelector from '../useAppSelector'
import useAppStore from '../useAppStore'
import { generateDataDogContext } from '../../utils/dataDog'
import { useLazyGetSession } from '../../graphQL/queries/getSession'
import { useLazyGetCart } from '../../graphQL/queries/getCart'
import { useLazyGetTotalPromotions } from '../../graphQL/queries/getTotalPromotions'
import {
  ISession,
  SessionDataArgs,
  ICheckoutRules,
} from '../../graphQL/queries/getSession/types'
import { setMessage, setShow } from '../../redux/slices/globalPromotions'
import { getMultipleTotalPromotionsLabel } from '../../utils/promotions/getTotalPromotionsLabel'
import { ITotalPromotions } from '../../graphQL/queries/getTotalPromotions/types'
import { CartProductResponse } from '../../graphQL/commons'
import getUrlParams from '../../utils/getUrlParams'
import { castCheckoutRulesMessages } from '../../utils/castCheckoutRules'

const { language } = getUrlParams(window.location.pathname)

const useFetchInitialCategoriesData = () => {
  const dispatch = useAppDispatch()
  const state = useAppSelector((reduxState) => reduxState)
  const store = useAppStore()
  const getAdvancedFiltersValues = useAdvancedFilters()
  const globalErrorConfig = {
    errorMessageTranslation: ErrorMessageTranslation.InitialData,
    showErrorTitle: true,
    globalError: true,
  }

  /**
   * The config as the store holds it now — see the twin in
   * `hooks/catalog/useInitialCatalogData.ts` for why, in full (COM-974).
   *
   * In short: `onSessionCompleted` below dispatches `setSessionData`, which
   * recomputes `config.options.hidePricesActive` from the session's customer,
   * and it does so from inside this hook's own `Promise.all`. Every price
   * string `onCartResponse` bakes reads that flag, so a render-time snapshot
   * bakes a hide-prices shopper's money into the store visibly.
   *
   * This twin fetches no catalogue, so it has no `castICatalogItems` call —
   * the exposure here is the cart's sanitized products and groups. Only
   * `hidePricesActive` can differ between the two reads; the rest of `state`
   * is deliberately still the snapshot.
   */
  const currentConfig = () => store.getState().defaultSlice.config

  const stateWithCurrentConfig = () => ({
    ...state,
    defaultSlice: { ...state.defaultSlice, config: currentConfig() },
  })

  const onCompleted = (
    sessionData: SessionDataArgs,
    cartData: CartProductResponse,
    totalPromotions?: ITotalPromotions,
  ) => {
    onCartResponse({
      data: cartData,
      sessionData,
      dispatch,
      state: stateWithCurrentConfig(),
    })

    if (totalPromotions?.getTotalPromotions?.length) {
      dispatch(
        setMessage(
          getMultipleTotalPromotionsLabel(
            totalPromotions?.getTotalPromotions,
            currentConfig(),
          ),
        ),
      )
      dispatch(setShow(true))
    }
  }

  const onError = (error: ApolloError) => {
    dispatch(manageErrors(globalErrorConfig))
    const context = generateDataDogContext({
      title: 'Get initial categories data got an error',
      extraInfo: { function: 'useFetchInitialCategoriesData > onError' },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(error, context)
  }

  const onSessionCompleted = ({ session }: ISession) => {
    dispatch(setSessionData(session))
    dispatch(setBanners(castBanners(session.banners || [])))
    if (session.configuration?.checkoutRules) {
      const lang =
        language || currentConfig()?.options?.localization || Localization.Es
      const checkoutRules = castCheckoutRulesMessages(
        session.configuration?.checkoutRules,
        lang,
      )
      dispatch(setCheckoutRules(checkoutRules as ICheckoutRules))
    }
    if (
      session.configuration?.filters?.promotions.active ||
      session.configuration?.filters?.categories.active ||
      session.configuration?.filters?.brands.active
    ) {
      getAdvancedFiltersValues()
    }
  }

  const [getSession] = useLazyGetSession({
    onCompleted: onSessionCompleted,
    onError,
  })

  const [getCart] = useLazyGetCart({
    onError,
  })

  const [getTotalPromotions] = useLazyGetTotalPromotions({
    onError,
  })

  /**
   * Whether this attempt populated the session and cart, so the caller can
   * decide whether to let a later one through (COM-974). Same contract as the
   * catalog twin, whose docstring carries the reasoning; the shapes differ
   * only in which queries have to land.
   */
  const fetchInitialDataAction = async (
    sessionId: string,
    storeName: string,
  ): Promise<boolean> => {
    dispatch(addLoader(LoadersType.INITIAL_DATA))

    try {
      if (!sessionId) {
        dispatch(manageErrors(globalErrorConfig))
        return false
      }
      return await Promise.all([
        getSession({
          variables: {
            storefrontName: storeName,
            sessionUid: sessionId,
          },
        }),
        getCart({
          variables: {
            cartStorefrontName: storeName,
            cartSessionUid: sessionId,
          },
        }),
        getTotalPromotions({
          variables: {
            storefrontName: storeName,
            sessionID: sessionId,
          },
        }),
      ]).then((values) => {
        const [
          { data: sessionData },
          { data: cartData },
          { data: totalPromotionsData },
        ] = values

        if (sessionData?.session && cartData?.cart) {
          onCompleted(sessionData.session, cartData.cart, totalPromotionsData)
          return true
        }

        /*
         * The one branch that used to be silent (COM-974) — see the catalog
         * twin for the reasoning.
         *
         * **Neither reason here is the silent one the twin found.** This hook
         * issues no `getCatalog` and none of its three queries sets
         * `errorPolicy: 'ignore'`, so a query that fails calls `onError` and
         * raises the error screen; what reaches this branch without one is a
         * 200 carrying a null `session` or `cart`. Both are defensive, and
         * saying so is the point — a zero on this counter means the shape has
         * not occurred, not that the hook is healthy.
         *
         * `getTotalPromotions` is deliberately absent from both the
         * conjunction and this report: it is not part of the conjunction
         * above, so an attempt that lands without it is a success here.
         */
        const missing: string[] = []
        if (!sessionData?.session) {
          missing.push('session')
        }
        if (!cartData?.cart) {
          missing.push('cart')
        }
        const context = generateDataDogContext({
          title: 'initial categories data incomplete',
          extraInfo: {
            function: 'fetchInitialCategoriesData',
            missing,
            storefrontName: storeName,
            sessionUid: sessionId,
          },
        })
        // Load-bearing rather than boilerplate: with manual views an error
        // raised while no view is active is dropped at assembly.
        datadogRum.startView(context.viewName)
        datadogRum.addError(
          new Error(
            `initial categories data settled without ${missing.join(
              ', ',
            )}; the storefront is left empty and the shopper is shown no error`,
          ),
          context,
        )
        return false
      })
    } catch (exception) {
      const context = generateDataDogContext({
        title: 'could not load initial categories data',
        extraInfo: { function: 'fetchInitialCategoriesData' },
      })
      datadogRum.startView(context.viewName)
      datadogRum.addError(exception, context)
      return false
    } finally {
      dispatch(removeLoader(LoadersType.INITIAL_DATA))
    }
  }

  return fetchInitialDataAction
}

export default useFetchInitialCategoriesData
